/**
 * Taskpane Chat Service
 * Handles communication with the orchestrator agent for the taskpane chat interface
 * 
 * Architecture:
 * - Each conversation has its own orchestrator stored in its AgentRegistry
 * - Messages are routed through the conversation's AgentRegistry
 * - LiveTracker is started on initialization to track slide changes
 */

import LivePowerPointOrchestrator from "../../main/agents/orchestrator";
import {
  authService,
  cancelConversationTasks,
  ensureRuntimeConversation,
  getRuntimeGlobalState,
  initializeChatServiceBase,
} from "@carousel-office/shared";
import { initializeAuthIntegration } from "../../main/globals/authIntegration";
import { UserInterviewTool } from "../../main/agents/tools/userInterview";
import { ReorderOrDeleteSlidesTool } from "../../main/agents/orchestrator/tools/reorderOrDeleteSlides";
import { AgentCallbacks } from "../../main/core/types";
import { liveTracker } from "../../services/tracking/liveTracker";
import { getOrCreatedocumentId } from "../../services/documentIdManager";
import { getDeckRegistry } from "../../services/tracking/deckRegistry";
import { hydrateMetadataForDecks } from "../../services/metadataService";
import { getSessionLogger } from "../../main/services/logging";
import { generateAndPersistTitle } from "../../main/utils/titleGenerator";
import { settingsService } from "../../services/settingsService";
import { externalFilesCache } from "../../services/externalFilesCache";

let isTaskpaneChatInitialized = false;
let taskpaneChatInitializationPromise: Promise<void> | null = null;
let startupLiveTrackingCleanupCompleted = false;

// Current presentation ID - set on initialization and used for new conversations
let taskpaneChatPresentationId: string | null = null;

// Event listener cleanup functions
let taskpaneChatInterviewCleanup: (() => void) | null = null;
let taskpaneChatSlideOpsCleanup: (() => void) | null = null;

// Track conversation titles (stores generated titles to avoid overwriting them)
// Key: conversationId, Value: generated title (or null if still using default)
const conversationTitles = new Map<string, string | null>();

/**
 * Initialize the taskpane chat service
 * Sets up GlobalStateManager, LiveTracker, and gets the current presentation ID
 */
export async function initializeTaskpaneChatService(): Promise<void> {
  if (isTaskpaneChatInitialized) {
    return;
  }

  if (taskpaneChatInitializationPromise) {
    return taskpaneChatInitializationPromise;
  }

  taskpaneChatInitializationPromise = (async () => {
    try {
    // Use shared base for auth, GlobalStateManager, PromptService, interview events
    const baseResult = await initializeChatServiceBase({
      serviceName: 'TaskpaneChatService',
      resolveDocumentId: async () => {
        try {
          return await getOrCreatedocumentId();
        } catch (error) {
          console.warn('[TaskpaneChatService] Could not get deck ID (presentation may not be open):', error);
          return null;
        }
      },
      initializeAuthIntegration: () => initializeAuthIntegration(),
      registerHostEventListeners: () => {
        const handleSlideOpsSubmit = (event: Event) => {
          const customEvent = event as CustomEvent<{ approvalId: string; decisions: [number, boolean][] }>;
          console.log('[TaskpaneChatService] Slide ops submit event received:', customEvent.detail);
          const decisionsMap = new Map<number, boolean>(customEvent.detail.decisions);
          ReorderOrDeleteSlidesTool.submitApproval(decisionsMap);
        };
        const handleSlideOpsCancel = (event: Event) => {
          const customEvent = event as CustomEvent<{ approvalId: string }>;
          console.log('[TaskpaneChatService] Slide ops cancel event received:', customEvent.detail);
          ReorderOrDeleteSlidesTool.cancelApproval('User cancelled the operation');
        };
        window.addEventListener('carousel:slide-ops-submit', handleSlideOpsSubmit);
        window.addEventListener('carousel:slide-ops-cancel', handleSlideOpsCancel);
        taskpaneChatSlideOpsCleanup = () => {
          window.removeEventListener('carousel:slide-ops-submit', handleSlideOpsSubmit);
          window.removeEventListener('carousel:slide-ops-cancel', handleSlideOpsCancel);
        };
        return taskpaneChatSlideOpsCleanup;
      },
      initializeRegistry: async () => {
        const deckRegistry = getDeckRegistry();
        await deckRegistry.initialize();
        const decks = deckRegistry.getAllDecks();
        await hydrateMetadataForDecks({
          documentIds: decks.map((deck) => ({
            documentId: deck.id,
            preferPptx: deck.isCurrent,
          })),
        });
      },
      startTracking: async () => {
        await liveTracker.initialize();
        await liveTracker.start();
      },
    });

    taskpaneChatPresentationId = baseResult.documentId;
    taskpaneChatInterviewCleanup = baseResult.interviewCleanup;

    // PPT-specific: One-time startup cleanup of stale live-tracking data
    if (!startupLiveTrackingCleanupCompleted && taskpaneChatPresentationId) {
      try {
        const retentionDays = settingsService.getConversationRetentionDays?.() || 7;
        const excludedocumentIds = [taskpaneChatPresentationId];
        const cleanupResult = await liveTracker.cleanupOldDeckTrackingData(retentionDays, { excludedocumentIds });
        startupLiveTrackingCleanupCompleted = true;
        console.log('[TaskpaneChatService] Live tracking retention cleanup complete:', {
          retentionDays,
          excludeddocumentIds: excludedocumentIds,
          ...cleanupResult,
        });
      } catch (error) {
        console.warn('[TaskpaneChatService] Failed live tracking retention cleanup at startup:', error);
      }
    }

    isTaskpaneChatInitialized = true;
    console.log('[TaskpaneChatService] Initialized successfully');
    } catch (error) {
      console.error('[TaskpaneChatService] Initialization failed:', error);
      // Reset initialization flag on error so we can retry
      isTaskpaneChatInitialized = false;
      taskpaneChatPresentationId = null;
      // Cleanup event listeners if set
      if (taskpaneChatInterviewCleanup) {
        taskpaneChatInterviewCleanup();
        taskpaneChatInterviewCleanup = null;
      }
      if (taskpaneChatSlideOpsCleanup) {
        taskpaneChatSlideOpsCleanup();
        taskpaneChatSlideOpsCleanup = null;
      }
      throw error;
    }
  })();

  try {
    await taskpaneChatInitializationPromise;
  } finally {
    taskpaneChatInitializationPromise = null;
  }
}

/**
 * Create a new conversation with its own orchestrator instance
 * The conversation inherits the current presentation ID and is registered in GlobalStateManager
 * 
 * @param conversationId - The unique ID for the conversation
 */
export async function createTaskpaneChatConversation(conversationId: string): Promise<void> {
  if (!isTaskpaneChatInitialized) {
    throw new Error('Taskpane chat service not initialized. Call initializeTaskpaneChatService() first.');
  }

  const globalState = getRuntimeGlobalState();
  
  // Get the current presentation ID (refresh it in case presentation changed)
  let presentationId = taskpaneChatPresentationId;
  try {
    presentationId = await getOrCreatedocumentId();
    taskpaneChatPresentationId = presentationId;
  } catch (error) {
    console.warn('[TaskpaneChatService] Could not refresh deck ID:', error);
    // Use the cached presentationId if available
    if (!presentationId) {
      presentationId = 'unknown_presentation';
    }
  }
  
  // Start session logging if not already started
  const logger = getSessionLogger();
  if (!logger.isSessionActive()) {
    logger.startSession();
    console.log('[TaskpaneChatService] Started session logging');
  }
  
  // Set presentation name in session metadata if available
  if (presentationId && presentationId !== 'unknown_presentation') {
    logger.setPresentationName(presentationId);
  }
  
  // Create the conversation with presentation ID and origin
  const conversation = ensureRuntimeConversation({
    globalState,
    conversationId,
    documentId: presentationId,
    origin: 'mainTaskpane',
  });
  
  // Create a new orchestrator instance for this conversation
  const orchestrator = new LivePowerPointOrchestrator();
  
  // Register the orchestrator in the conversation's AgentRegistry
  conversation.agentRegistry.registerAgent('livePowerPointOrchestrator', orchestrator);
  
  console.log(`[TaskpaneChatService] Created conversation ${conversationId} with orchestrator for presentation ${presentationId}`);
}

/**
 * Cancel any pending user interview
 * Call this when user sends a new message instead of answering the interview
 */
export function cancelTaskpaneChatInterview(): void {
  if (UserInterviewTool.hasPendingInterview()) {
    console.log('[TaskpaneChatService] Cancelling pending interview - user sent new message');
    UserInterviewTool.cancelInterview(
      "User didn't respond to your questions. Instead, they elected to continue chatting. Please continue the conversation with them."
    );
  }
}

/**
 * Send a message to the orchestrator agent for a specific conversation
 * Routes through the conversation's AgentRegistry to get the correct orchestrator instance
 * 
 * @param message - The user message to send
 * @param conversationId - The conversation ID to route the message to
 * @param callbacks - Optional callbacks for streaming and operation updates
 * @returns The full response from the orchestrator
 */
export async function sendTaskpaneChatMessage(
  message: string,
  conversationId: string,
  callbacks?: AgentCallbacks,
  context?: Array<{
    context: string[];
    purpose: string;
  }>
): Promise<{ content: string; message: string }> {
  if (!isTaskpaneChatInitialized) {
    throw new Error('Taskpane chat service not initialized. Call initializeTaskpaneChatService() first.');
  }

  const globalState = getRuntimeGlobalState();
  const conversation = globalState.getConversation(conversationId);
  
  if (!conversation) {
    throw new Error(`Conversation ${conversationId} not found. Call createTaskpaneChatConversation() first.`);
  }
  
  if (conversation.conversationOrigin !== 'mainTaskpane') {
    throw new Error(`Conversation ${conversationId} is not a mainTaskpane conversation (origin: ${conversation.conversationOrigin})`);
  }
  
  const orchestrator = conversation.agentRegistry.getAgent(
    'livePowerPointOrchestrator'
  ) as LivePowerPointOrchestrator | null;
  
  if (!orchestrator) {
    throw new Error(`No orchestrator found for conversation ${conversationId}`);
  }

  try {
    console.log(`[TaskpaneChatService] Sending message to conversation ${conversationId}:`, message);

    try {
      // Keep external file index mapping fresh before reminder/context injection.
      await externalFilesCache.ensureFresh();
    } catch (externalFilesError) {
      console.warn(
        "[TaskpaneChatService] External files cache refresh failed before send:",
        externalFilesError
      );
    }
    
    // Session logging: log user message
    const logger = getSessionLogger();
    logger.logUserMessage(message);
    
    // Execute orchestrator with message, conversationId, and documentId from conversation
    const result = await orchestrator.execute({
      message,
      context,
      conversationId: conversation.id,
      documentId: conversation.presentationId,
    }, callbacks);
    
    console.log('[TaskpaneChatService] Received response:', result);
    
    // Type assertion: orchestrator returns { response: { content: string, message: string } }
    const response = result.response as { content: string; message: string };
    
    // Check if this is the first turn and we should generate a title
    const isFirstTurn = !conversationTitles.has(conversationId);
    
    // Get the title to use for persistence
    const getTitle = (): string => {
      const storedTitle = conversationTitles.get(conversationId);
      if (storedTitle) {
        return storedTitle;
      }
      return `Taskpane - ${new Date().toLocaleDateString()}`;
    };
    
    if (isFirstTurn) {
      // Mark that we're tracking this conversation (null = title generation pending)
      conversationTitles.set(conversationId, null);
      
      // Fire off title generation async (don't await - don't block the response)
      generateAndPersistTitle(
        conversationId, 
        message,
        // Callback to store the generated title for future persists
        (convId, title) => {
          conversationTitles.set(convId, title);
          console.log(`[TaskpaneChatService] Stored generated title for ${convId}: "${title}"`);
        }
      ).catch(err => {
        console.warn('[TaskpaneChatService] Title generation failed (non-blocking):', err);
      });
      
      // Still persist with default title for now (title generation will update it)
      try {
        await globalState.persistConversation(conversationId, {
          title: getTitle(),
          status: 'active',
        });
        console.log('[TaskpaneChatService] Auto-persisted conversation (title generation in progress)');
      } catch (persistError) {
        console.warn('[TaskpaneChatService] Failed to auto-persist conversation:', persistError);
      }
    } else {
      // Not first turn - persist with the stored title (or default if generation failed/pending)
      try {
        await globalState.persistConversation(conversationId, {
          title: getTitle(),
          status: 'active',
        });
        console.log('[TaskpaneChatService] Auto-persisted conversation for debugging');
      } catch (persistError) {
        console.warn('[TaskpaneChatService] Failed to auto-persist conversation:', persistError);
      }
    }
    
    return response;
  } catch (error) {
    console.error('[TaskpaneChatService] Error sending message:', error);
    throw error;
  }
}

/**
 * Cancel any running task for a conversation
 * This sets the cancellation token so the agent will stop at the next check point
 * 
 * @param conversationId - The conversation ID to cancel tasks for
 */
export function cancelTaskpaneChatTask(conversationId: string): void {
  if (!isTaskpaneChatInitialized) {
    console.warn('[TaskpaneChatService] Cannot cancel task - service not initialized');
    return;
  }

  const globalState = getRuntimeGlobalState();
  const conversation = globalState.getConversation(conversationId);
  
  if (!conversation) {
    console.warn(`[TaskpaneChatService] Cannot cancel task - conversation ${conversationId} not found`);
    return;
  }
  
  const runningTasks = cancelConversationTasks(
    conversation.agentRegistry,
    conversationId,
    ['running', 'pending']
  );
  
  if (runningTasks.length === 0) {
    console.log(`[TaskpaneChatService] No running tasks to cancel for conversation ${conversationId}`);
    return;
  }
  
  for (const task of runningTasks) {
    console.log(`[TaskpaneChatService] 🛑 Cancelled task ${task.taskId} (${task.agentName})`);
  }
  
  console.log(`[TaskpaneChatService] Cancelled ${runningTasks.length} task(s) for conversation ${conversationId}`);
}

/**
 * Check if the taskpane chat service is initialized
 */
export function isTaskpaneChatServiceInitialized(): boolean {
  return isTaskpaneChatInitialized;
}

/**
 * Get the current presentation ID
 */
export function getTaskpaneChatPresentationId(): string | null {
  return taskpaneChatPresentationId;
}

